/**
 * Add to / repay a debt, add to / withdraw a trust (D03 §2 flow 2; old `handleAdjustDebt` App.tsx:21360-21907) over the
 * gateway: debt add → `customer_withdraw` (cash out, the debt grows), debt repay → `customer_deposit`, trust add →
 * `customer_deposit`, trust withdraw → `customer_withdraw`. A debounced dry run feeds the live balance panel (old
 * `getRealTimeBalanceDetails`, client arithmetic on stored balances → the server's `applied`); when the engine answers
 * `SURPLUS_ACCOUNT_REQUIRED` the confirm button opens the old surplus step, whose answer is posted inside the same
 * operation (D-073 pattern, D-218 (a)); a new account there is opened with the PIN of a `create_customers` holder
 * (D-018). USD goes through the dollar vault only (D-029).
 */
import { useEffect, useRef, useState } from "react";
import { CustomerAdjustModal } from "@faylasuf/ui";
import type { CustomerAccountRow, PreviewResult } from "../../api/types.js";
import { useApp } from "../../app/context.js";
import { engineErrors, errorMessage } from "../../app/errors.js";
import { useGateway } from "../../app/operations.js";
import { useToast } from "../../app/toast.js";
import type { CustomerBoxes } from "./boxes.js";
import { accountIdOf, balanceOf, balancesLine, decAdd, decSub, fmt, positive } from "./format.js";
import { EMPTY_SURPLUS, resolveSurplus, surplusTargets, type SurplusState } from "./surplus.js";

export function adjustType(kind: "debt" | "trust", action: "add" | "withdraw"): "customer_deposit" | "customer_withdraw" {
  if (kind === "debt") return action === "add" ? "customer_withdraw" : "customer_deposit";
  return action === "add" ? "customer_deposit" : "customer_withdraw";
}

export function AdjustContainer({ row, rows, boxes, usdHidden, initialAction, onClose }: { row: CustomerAccountRow; rows: readonly CustomerAccountRow[]; boxes: CustomerBoxes; usdHidden: boolean; initialAction: "add" | "withdraw"; onClose: () => void }) {
  const { t } = useApp();
  const toast = useToast();
  const gateway = useGateway();
  const [currency, setCurrency] = useState<"IQD" | "USD">("IQD");
  const [action, setAction] = useState<"add" | "withdraw">(initialAction);
  const [amount, setAmount] = useState("");
  const [boxId, setBoxId] = useState("");
  const [note, setNote] = useState("");
  const [preview, setPreview] = useState<PreviewResult | null>(null);
  const [surplusNeeded, setSurplusNeeded] = useState<string | null>(null);
  const [surplusStep, setSurplusStep] = useState(false);
  const [surplus, setSurplus] = useState<SurplusState>(EMPTY_SURPLUS);
  const [busy, setBusy] = useState(false);
  const seq = useRef(0);

  const type = adjustType(row.kind, action);
  const accountId = accountIdOf(row, currency);
  const input = positive(amount) && boxId ? { accountId, amount, boxAccountId: boxId, ...(note.trim() ? { note: note.trim() } : {}) } : null;
  const decreasing = (row.kind === "debt" && action === "withdraw") || (row.kind === "trust" && action === "withdraw");
  const targetKind = row.kind === "debt" ? "trust" : "debt";

  // Debounced dry run (the figures the panel shows are the server's).
  useEffect(() => {
    setPreview(null);
    setSurplusNeeded(null);
    if (!input) return;
    const my = ++seq.current;
    const h = setTimeout(() => {
      gateway.preview(type, input).then(
        (pv) => {
          if (my === seq.current) setPreview(pv);
        },
        (e: unknown) => {
          if (my !== seq.current) return;
          const s = engineErrors(e).find((x) => x.code === "SURPLUS_ACCOUNT_REQUIRED");
          if (s) setSurplusNeeded(String(s.params["surplus"] ?? ""));
        },
      );
    }, 350);
    return () => clearTimeout(h);
  }, [type, accountId, amount, boxId, note]);

  const unit = currency === "USD" ? "$" : t("unit.iqd");
  const before = balanceOf(row, currency);
  const applied = preview?.derived["applied"] ?? (surplusNeeded !== null ? before : null);
  const live = applied !== null && positive(amount)
    ? { beforeLabel: `${fmt(before)} ${unit}`, amountLabel: `${fmt(amount)} ${unit}`, afterLabel: `${fmt(decreasing ? decSub(before, applied) : decAdd(before, applied))} ${unit}` }
    : null;

  const pinMessage = (newName: string | null) =>
    newName ? t("cust.pin_new_account", { name: newName }) : t("cust.pin_adjust", { action: action === "add" ? (row.kind === "trust" ? t("addTrust") : t("addDebt")) : row.kind === "trust" ? t("withdrawTrust") : t("repayDebt"), name: row.name });

  const confirm = async () => {
    if (!input) return;
    if (surplusNeeded !== null) {
      setSurplusStep(true);
      return;
    }
    setBusy(true);
    try {
      let pv = preview;
      if (!pv) {
        try {
          pv = await gateway.preview(type, input);
        } catch (e) {
          toast(errorMessage(t, e), "error");
          return;
        }
      }
      const res = await gateway.commitPreview(pv, { pinMessage: pinMessage(null) });
      if (res) onClose();
    } catch {
      /* toasted by the gateway */
    } finally {
      setBusy(false);
    }
  };

  const confirmSurplus = async () => {
    if (!input) return;
    const r = resolveSurplus(surplus, row, rows, targetKind, currency);
    if (!r.ok) {
      toast(t(r.messageKey), "error");
      return;
    }
    setBusy(true);
    try {
      let pv: PreviewResult;
      try {
        pv = await gateway.previewWithNew(type, { ...input, surplus: { kind: "account", accountId: r.accountId } }, r.newAccounts);
      } catch (e) {
        toast(errorMessage(t, e), "error");
        return;
      }
      const res = await gateway.commitPreview(pv, { pinMessage: pinMessage(r.newName) });
      if (res) onClose();
    } catch {
      /* toasted by the gateway */
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <CustomerAdjustModal
        name={row.name}
        kind={row.kind}
        balancesLabel={balancesLine(t, row, usdHidden)}
        currency={currency}
        onCurrencyChange={(c) => {
          setCurrency(c);
          setBoxId(c === "USD" ? boxes.dollarVaultId : "");
        }}
        usdHidden={usdHidden}
        action={action}
        onActionChange={setAction}
        amount={amount}
        onAmountChange={setAmount}
        boxValue={boxId}
        onBoxChange={setBoxId}
        boxOptions={currency === "USD" ? boxes.usd : boxes.iqd}
        live={live}
        note={note}
        onNoteChange={setNote}
        canConfirm={input !== null}
        onConfirm={() => void confirm()}
        onClose={onClose}
        surplus={
          surplusStep && surplusNeeded !== null
            ? {
                type: row.kind === "debt" ? "debt_overpaid" : "trust_overdrawn",
                amountLabel: `${fmt(amount)} ${currency}`,
                originalLabel: `${fmt(before)} ${currency}`,
                surplusLabel: `${fmt(surplusNeeded || decSub(amount, before))} ${currency}`,
              }
            : null
        }
        surplusProps={{
          choice: surplus.choice,
          onChoice: (choice) => setSurplus((s) => ({ ...s, choice })),
          targets: surplusTargets(rows, [row.id], targetKind, currency, t),
          targetId: surplus.targetId,
          onTarget: (targetId) => setSurplus((s) => ({ ...s, targetId })),
          newName: surplus.newName,
          onNewName: (newName) => setSurplus((s) => ({ ...s, newName })),
        }}
        onSurplusConfirm={() => void confirmSurplus()}
        onSurplusBack={() => setSurplusStep(false)}
        busy={busy}
      />
      {gateway.pin.dialog}
    </>
  );
}
